export interface Pt { x: number; y: number; }
export interface CentroidSize {
  center: Pt;
  size: number;
}

/**
 * Bir parsel poligonunun (örneklenmiş noktalarına göre) merkezi + tek rozet
 * boyutu — parselin GERÇEK şekline göre, TAŞMADAN. PCA ile ana/dar eksen
 * yarı-uzunlukları bulunur; rozet boyutu dar kenara göre sınırlanır ki eğik/
 * ince şeritlerde de rozet parselin dışına taşmasın.
 */
export function centroidAndSize(points: Pt[], minSize = 9, maxSize = 24): CentroidSize {
  const n = points.length || 1;
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;

  let sxx = 0, syy = 0, sxy = 0;
  for (const p of points) {
    const vx = p.x - cx, vy = p.y - cy;
    sxx += vx * vx; syy += vy * vy; sxy += vx * vy;
  }
  sxx /= n; syy /= n; sxy /= n;
  const ang = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const ux = Math.cos(ang), uy = Math.sin(ang);
  const wx = -uy, wy = ux;

  let majorHalf = 0, minorHalf = 0;
  for (const p of points) {
    const vx = p.x - cx, vy = p.y - cy;
    const a = Math.abs(vx * ux + vy * uy);
    const b = Math.abs(vx * wx + vy * wy);
    if (a > majorHalf) majorHalf = a;
    if (b > minorHalf) minorHalf = b;
  }

  const size = Math.max(minSize, Math.min(maxSize, Math.min(minorHalf * 1.5, majorHalf * 0.9)));
  return { center: { x: cx, y: cy }, size };
}

/** Marina su havzasının yaklaşık merkezi (SVG koord.) — "ön" = suya bakan yön. */
export const WATER_POINT: Pt = { x: 1160, y: 250 };

export interface ParcelGeom {
  center: Pt;
  /** Ana eksen boyunca suya (front) doğru BİRİM vektör */
  front: Pt;
  /** Ana eksende merkezden uca yarı-uzunluk */
  majorHalf: number;
  size: number;
}

/**
 * Parselin merkezi + rozet boyutu + parselin KENDİ uzun ekseni (front↔back) +
 * o eksende yarı-uzunluk. Ön yön PCA ana ekseni (parselin derinliği) olarak
 * alınır ve suya doğru çevrilir. Rozetler bu eksen boyunca ön/orta/arka
 * konumlanır; GENİŞLİK ekseninde daima ortada (yana kaymaz → parseller arası
 * çizgiye taşmaz).
 */
export function parcelGeom(points: Pt[], waterPoint: Pt = WATER_POINT, minSize = 9, maxSize = 22): ParcelGeom {
  const n = points.length || 1;
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;

  // PCA → ana (uzun) eksen = parselin derinlik yönü (onaylı yerleşim)
  let sxx = 0, syy = 0, sxy = 0;
  for (const p of points) {
    const vx = p.x - cx, vy = p.y - cy;
    sxx += vx * vx; syy += vy * vy; sxy += vx * vy;
  }
  sxx /= n; syy /= n; sxy /= n;
  const ang = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  let ux = Math.cos(ang), uy = Math.sin(ang);
  if (ux * (waterPoint.x - cx) + uy * (waterPoint.y - cy) < 0) { ux = -ux; uy = -uy; }
  const wx = -uy, wy = ux;

  let majorHalf = 0, minorHalf = 0;
  for (const p of points) {
    const vx = p.x - cx, vy = p.y - cy;
    const a = Math.abs(vx * ux + vy * uy);
    const b = Math.abs(vx * wx + vy * wy);
    if (a > majorHalf) majorHalf = a;
    if (b > minorHalf) minorHalf = b;
  }
  const size = Math.max(minSize, Math.min(maxSize, minorHalf * 1.35));
  return { center: { x: cx, y: cy }, front: { x: ux, y: uy }, majorHalf, size };
}

/**
 * Bir ayraç çizgisinin (iki parsel arasındaki sınır parçası) iki yanındaki
 * yoklama noktaları: orta noktadan dik yönde ±delta. `tools/split-parcels.mjs`
 * ayraçları üretirken aynı yöntemi kullanıyor (DELTA = 4.5), burada da aynı
 * mesafe kullanılır ki noktalar komşu parsellerin içine düşsün.
 */
export function dividerProbes(a: Pt, b: Pt, delta = 4.5): [Pt, Pt] {
  const ex = b.x - a.x, ey = b.y - a.y;
  const len = Math.hypot(ex, ey) || 1;
  const nx = -ey / len, ny = ex / len;
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  return [
    { x: mx + nx * delta, y: my + ny * delta },
    { x: mx - nx * delta, y: my - ny * delta },
  ];
}
